import { useState, useEffect } from 'react';
import { Plus, Trash2, Clock, AlertCircle } from 'lucide-react';
import { Button, Select, Card, Alert, Badge, LoadingSpinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import type { DeliverySchedule, DeliverySchedulesResponse } from '@/types';

const timeOptions = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: `${hour}:00`, label: `${hour}:00` };
});

const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'UTC', label: 'UTC' },
];

// Timezone UTC offsets (approximate, doesn't account for DST perfectly but good enough for validation)
const timezoneOffsets: Record<string, number> = {
  'America/New_York': -5,
  'America/Chicago': -6,
  'America/Denver': -7,
  'America/Los_Angeles': -8,
  'Europe/London': 0,
  'Europe/Paris': 1,
  'Asia/Tokyo': 9,
  'UTC': 0,
};

/**
 * Get approximate UTC hour from local time and timezone.
 * Used for client-side validation to prevent scheduling conflicts.
 */
function getApproxUtcHour(localTime: string, timezone: string): number {
  const localHour = parseInt(localTime.split(':')[0], 10);
  const offset = timezoneOffsets[timezone] ?? 0;
  const utcHour = (localHour - offset + 24) % 24;
  return utcHour;
}

/**
 * Check if a new schedule would conflict with existing schedules.
 * Returns the conflicting schedule's label/time if found, null otherwise.
 */
function findConflictingSchedule(
  schedules: DeliverySchedule[],
  newTime: string,
  newTimezone: string,
  excludeScheduleId?: string
): DeliverySchedule | null {
  const newUtcHour = getApproxUtcHour(newTime, newTimezone);

  for (const schedule of schedules) {
    if (excludeScheduleId && schedule.id === excludeScheduleId) continue;

    const existingUtcHour = getApproxUtcHour(schedule.delivery_time, schedule.timezone);
    if (newUtcHour === existingUtcHour) {
      return schedule;
    }
  }
  return null;
}

interface Props {
  onScheduleChange?: () => void;
}

export function DeliveryScheduleManager({ onScheduleChange }: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<DeliverySchedulesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // New schedule form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTime, setNewTime] = useState('08:00');
  const [newTimezone, setNewTimezone] = useState('America/New_York');
  const [newLabel, setNewLabel] = useState('');

  const loadSchedules = async () => {
    try {
      const response = await api.getDeliverySchedules();
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedules');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSchedules();
  }, []);

  const handleAddSchedule = async () => {
    // Client-side validation for duplicate times
    const conflict = findConflictingSchedule(schedules, newTime, newTimezone);
    if (conflict) {
      setError(`A newsletter is already scheduled at this time (${conflict.delivery_time} ${conflict.timezone_abbr || conflict.timezone}). Please choose a different time.`);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await api.addDeliverySchedule({
        delivery_time: newTime,
        timezone: newTimezone,
        label: newLabel || undefined,
        is_active: true,
      });
      await loadSchedules();
      setShowAddForm(false);
      setNewTime('08:00');
      setNewLabel('');
      onScheduleChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add schedule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;
    
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteDeliverySchedule(scheduleId);
      await loadSchedules();
      onScheduleChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete schedule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateSchedule = async (schedule: DeliverySchedule, updates: Partial<DeliverySchedule>) => {
    // Client-side validation for duplicate times
    const newTime = updates.delivery_time || schedule.delivery_time;
    const newTimezone = updates.timezone || schedule.timezone;
    const conflict = findConflictingSchedule(schedules, newTime, newTimezone, schedule.id);
    if (conflict) {
      setError(`A newsletter is already scheduled at this time (${conflict.delivery_time} ${conflict.timezone_abbr || conflict.timezone}). Please choose a different time.`);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await api.updateDeliverySchedule(schedule.id, {
        delivery_time: newTime,
        timezone: newTimezone,
        label: updates.label !== undefined ? updates.label : schedule.label,
        is_active: updates.is_active !== undefined ? updates.is_active : schedule.is_active,
      });
      await loadSchedules();
      onScheduleChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update schedule');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  const schedules = data?.schedules || [];
  const canAddMore = data?.can_add_more ?? false;
  const maxSchedules = data?.max_schedules ?? 1;

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Schedule List */}
      <div className="space-y-3">
        {schedules.map((schedule) => (
          <ScheduleItem
            key={schedule.id}
            schedule={schedule}
            canDelete={schedules.length > 1}
            onUpdate={(updates) => handleUpdateSchedule(schedule, updates)}
            onDelete={() => handleDeleteSchedule(schedule.id)}
            isDisabled={isSaving}
          />
        ))}
      </div>

      {/* Add Schedule Button/Form */}
      {canAddMore && !showAddForm && (
        <Button
          variant="secondary"
          size="sm"
          className="w-full"
          onClick={() => setShowAddForm(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Newsletter Time
        </Button>
      )}

      {/* Add Schedule Form */}
      {showAddForm && (
        <Card className="p-4 border-primary/50">
          <h4 className="font-medium mb-3">Add Newsletter Time</h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Select
              label="Time"
              options={timeOptions}
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
            />
            <Select
              label="Timezone"
              options={timezoneOptions}
              value={newTimezone}
              onChange={(e) => setNewTimezone(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddSchedule} isLoading={isSaving}>
              Add Schedule
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Upgrade Prompt */}
      {!canAddMore && schedules.length >= maxSchedules && maxSchedules < 3 && (
        <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <AlertCircle className="w-5 h-5 text-primary mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium">Want more newsletter times?</p>
            <p className="text-sm text-text-secondary mt-1">
              Upgrade to Standard or Professional for up to 3 newsletters per day.
            </p>
            <Button
              size="sm"
              variant="primary"
              className="mt-2"
              onClick={() => navigate('/dashboard/subscription')}
            >
              View Plans
            </Button>
          </div>
        </div>
      )}

      {/* Info */}
      <p className="text-xs text-text-muted">
        {schedules.length} of {maxSchedules} newsletter{maxSchedules > 1 ? 's' : ''} per day configured
      </p>
    </div>
  );
}

// Schedule Item Component
interface ScheduleItemProps {
  schedule: DeliverySchedule;
  canDelete: boolean;
  onUpdate: (updates: Partial<DeliverySchedule>) => void;
  onDelete: () => void;
  isDisabled: boolean;
}

function ScheduleItem({ schedule, canDelete, onUpdate, onDelete, isDisabled }: ScheduleItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTime, setEditTime] = useState(schedule.delivery_time);
  const [editTimezone, setEditTimezone] = useState(schedule.timezone);

  const handleSave = () => {
    onUpdate({ delivery_time: editTime, timezone: editTimezone });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="p-3 bg-surface-light rounded-lg border border-border">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Select
            label="Time"
            options={timeOptions}
            value={editTime}
            onChange={(e) => setEditTime(e.target.value)}
          />
          <Select
            label="Timezone"
            options={timezoneOptions}
            value={editTimezone}
            onChange={(e) => setEditTimezone(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isDisabled}>Save</Button>
          <Button size="sm" variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 p-3 bg-surface-light rounded-lg border border-border">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Clock className="w-4 h-4 text-primary flex-shrink-0" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{schedule.delivery_time}</span>
            <Badge variant="info" className="text-xs">
              {schedule.timezone_abbr || schedule.timezone}
            </Badge>
            {schedule.label && (
              <span className="text-xs text-text-muted truncate">({schedule.label})</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} disabled={isDisabled}>
          Edit
        </Button>
        {canDelete && (
          <Button size="sm" variant="ghost" onClick={onDelete} disabled={isDisabled}>
            <Trash2 className="w-4 h-4 text-error" />
          </Button>
        )}
      </div>
    </div>
  );
}

